/* src/net/pollingScheduler.ts — the game-state poll loop (TASKS.md 2.5, PLAN.md Part 2 §8).
 *
 * There is no websocket (RECON §3): the client asks, repeatedly, and the server folds elapsed
 * time forward when it does. That makes this loop the game's heartbeat — and also the thing
 * that must not melt a laptop in a background tab.
 *
 * Three behaviours the naive `setInterval(fetch, 3000)` gets wrong:
 *   • **Hidden tabs.** A backgrounded tab polls far more slowly. The simulation does not
 *     suffer for it: `advance_player()` is a lazy catch-up, so a longer gap is folded forward
 *     on the next poll rather than lost.
 *   • **Overlap.** The next tick is scheduled after the previous one *settles*, never on a
 *     fixed interval — otherwise a slow response has requests stacking on each other.
 *   • **Failure.** A failed poll backs off exponentially and keeps trying. It must not kill
 *     the loop: the world stays on screen and the HUD keeps interpolating from the last known
 *     rate, which is exactly the graceful degradation PLAN.md Part 2 §8 asks for.
 */

import { gameStore } from '../core/store.ts';
import { incomeInterpolator } from '../sim/incomeInterpolator.ts';
import { fetchGameState } from './gameApi.ts';

export interface PollingOptions {
  /** Interval while the tab is visible, ms. */
  readonly activeMs?: number;
  /** Interval while `document.hidden`, ms. */
  readonly hiddenMs?: number;
  /** Ceiling for the error backoff, ms. */
  readonly maxBackoffMs?: number;
  /** Called on every failure; the loop continues regardless. */
  readonly onError?: (err: unknown) => void;
}

const DEFAULTS = {
  activeMs: 4_000,
  hiddenMs: 60_000,
  maxBackoffMs: 60_000,
} as const;

export class PollingScheduler {
  readonly #opts: Required<Omit<PollingOptions, 'onError'>> & Pick<PollingOptions, 'onError'>;
  #timer: number | null = null;
  #running = false;
  #inFlight = false;
  #failures = 0;

  constructor(options: PollingOptions = {}) {
    this.#opts = {
      activeMs: options.activeMs ?? DEFAULTS.activeMs,
      hiddenMs: options.hiddenMs ?? DEFAULTS.hiddenMs,
      maxBackoffMs: options.maxBackoffMs ?? DEFAULTS.maxBackoffMs,
      ...(options.onError ? { onError: options.onError } : {}),
    };
  }

  start(): void {
    if (this.#running) return;
    this.#running = true;
    document.addEventListener('visibilitychange', this.#onVisibility);
    void this.#tick();
  }

  stop(): void {
    this.#running = false;
    if (this.#timer !== null) {
      window.clearTimeout(this.#timer);
      this.#timer = null;
    }
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  /** Poll immediately — used after a mutation, and when a hidden tab comes back to the front. */
  async pollNow(): Promise<void> {
    if (this.#timer !== null) {
      window.clearTimeout(this.#timer);
      this.#timer = null;
    }
    await this.#tick();
  }

  #onVisibility = (): void => {
    // Coming back to a foreground tab: refresh at once rather than waiting out the long
    // hidden-tab interval, so the player never looks at a stale balance.
    if (!document.hidden && this.#running) void this.pollNow();
  };

  async #tick(): Promise<void> {
    if (!this.#running || this.#inFlight) return;
    this.#inFlight = true;

    try {
      const state = await fetchGameState();
      // Interpolator first: it reads the value currently on screen to compute its correction,
      // and the store write is what triggers the HUD to redraw.
      incomeInterpolator.sync(state);
      gameStore.set(state);
      this.#failures = 0;
    } catch (err) {
      this.#failures += 1;
      this.#opts.onError?.(err);
    } finally {
      this.#inFlight = false;
      this.#schedule();
    }
  }

  #schedule(): void {
    if (!this.#running) return;
    const base = document.hidden ? this.#opts.hiddenMs : this.#opts.activeMs;
    const delay =
      this.#failures === 0
        ? base
        : Math.min(this.#opts.maxBackoffMs, base * 2 ** Math.min(this.#failures, 6));
    this.#timer = window.setTimeout(() => {
      void this.#tick();
    }, delay);
  }
}
